import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { px } from "@/components/tool-kit/charts/marks";
import {
  colorIndex,
  paletteColor,
  textColorOn,
} from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  percentText,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  LINE_HEIGHT,
  layoutLegend,
  layoutTitle,
} from "@/lib/tool-kit/charts/layout";
import {
  arcPath,
  type PieLayout,
  sliceLabelPositions,
} from "@/lib/tool-kit/charts/pie";
import { estimateTextWidth, maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A pie or donut chart from `layoutPie`, with its legend beside it.
 *
 * Slices start at 12 o'clock and run clockwise, separated by a thin white gap. A slice
 * label goes inside when it fits and just outside the rim when it does not, never on
 * top of a neighbour's. The "Other" slice is gray so it never competes with a real
 * category for a palette colour.
 */

export type SliceLabelMode = "percent" | "value" | "both" | "none";

const OTHER_COLOR = CHART_INK.axis;

export function sliceText(
  mode: SliceLabelMode,
  value: number,
  share: number,
  fmt: NumberFormat
): string {
  const percent = percentText(fmt, share);
  const amount = valueText(fmt, value);
  if (mode === "percent") {
    return percent;
  }
  if (mode === "value") {
    return amount;
  }
  return mode === "both" ? `${amount} (${percent})` : "";
}

function DonutTotal({
  cx,
  cy,
  label,
  total,
}: {
  cx: number;
  cy: number;
  label: string;
  total: string;
}) {
  return (
    <g>
      <text
        fill={CHART_INK.muted}
        fontSize={CHART_FONT_SIZE.tick}
        textAnchor="middle"
        x={px(cx)}
        y={px(cy - 4)}
      >
        {label}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={CHART_FONT_SIZE.title}
        fontWeight="bold"
        textAnchor="middle"
        x={px(cx)}
        y={px(cy + 16)}
      >
        {total}
      </text>
    </g>
  );
}

export function PieChart({
  id,
  pie,
  donut,
  labelMode,
  style,
  fmt,
  totalLabel,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  pie: PieLayout;
  donut: boolean;
  labelMode: SliceLabelMode;
  style: ChartStyle;
  fmt: NumberFormat;
  /** Shown above the total in a donut's hole. */
  totalLabel: string;
  label: string;
  description: string;
}) {
  const width = CHART_WIDTH;
  const height = CHART_HEIGHT;
  const title = style.apa ? null : layoutTitle(style.title, width);
  const top = CHART_PADDING + (title?.height ?? 0);
  const names = pie.slices.map((slice) => slice.label);
  const legendWidth = Math.min(
    220,
    maxTextWidth(names, { fontSize: CHART_FONT_SIZE.legend }) + 24
  );
  const legend = layoutLegend(names, legendWidth, { oneColumn: true });
  const pieWidth = width - 2 * CHART_PADDING - legendWidth - 24;
  const pieHeight = height - top - CHART_PADDING;
  // Room beside the circle for labels that have to sit outside a thin slice.
  const room = labelMode === "none" ? 6 : 56;
  const outer = Math.max(40, Math.min(pieWidth / 2 - room, pieHeight / 2 - 22));
  const inner = donut ? outer * 0.56 : 0;
  const cx = CHART_PADDING + pieWidth / 2;
  const cy = top + pieHeight / 2;
  const start = colorIndex(style.color);
  const colors = pie.slices.map((slice, index) =>
    slice.other ? OTHER_COLOR : paletteColor(start, index)
  );
  const fontSize = CHART_FONT_SIZE.value;
  const texts = pie.slices.map((slice) =>
    sliceText(labelMode, slice.value, slice.share, fmt)
  );
  const positions =
    labelMode === "none"
      ? []
      : sliceLabelPositions(pie.slices, {
          cx,
          cy,
          outer,
          inner,
          lineHeight: fontSize * LINE_HEIGHT + 2,
          fits: (index, arc) =>
            estimateTextWidth(texts[index], { fontSize }) <= arc * 0.85 &&
            (inner === 0 || outer - inner >= fontSize + 6),
        });
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={width}
    >
      <ChartTitle title={title} />
      {pie.slices.map((slice, index) => (
        <path
          d={arcPath(cx, cy, outer, inner, slice.startAngle, slice.endAngle)}
          fill={colors[index]}
          key={`${slice.label}-${slice.startAngle}`}
          stroke={CHART_INK.background}
          strokeLinejoin="round"
          strokeWidth={pie.slices.length > 1 ? 1.5 : 0}
        />
      ))}
      {positions.map((position) =>
        position.rim ? (
          <line
            key={`leader-${position.index}`}
            stroke={CHART_INK.axis}
            strokeWidth={0.75}
            x1={px(position.rim[0])}
            x2={px(position.x + (position.anchor === "start" ? -3 : 3))}
            y1={px(position.rim[1])}
            y2={px(position.y)}
          />
        ) : null
      )}
      {positions.map((position) => (
        <text
          fill={
            position.inside
              ? textColorOn(colors[position.index])
              : CHART_INK.text
          }
          fontSize={fontSize}
          key={`label-${position.index}`}
          textAnchor={position.anchor}
          x={px(position.x)}
          y={px(position.y + fontSize * 0.35)}
        >
          {texts[position.index]}
        </text>
      ))}
      {donut ? (
        <DonutTotal
          cx={cx}
          cy={cy}
          label={totalLabel}
          total={valueText(fmt, pie.total)}
        />
      ) : null}
      <SvgLegend
        keys={colors.map((color) => ({ color, kind: "swatch" }))}
        layout={legend}
        x={width - CHART_PADDING - legendWidth}
        y={Math.max(top, cy - legend.height / 2)}
      />
    </ChartSvg>
  );
}
